import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  TextField, 
  MenuItem, 
  FormControlLabel, 
  Switch, 
  Typography, 
  Box,
  Select,
  FormControl,
  SelectChangeEvent
} from '@mui/material';
import { 
  StyledDialog, 
  StyledDialogTitle, 
  StyledDialogContent, 
  StyledDialogActions, 
  CancelButton, 
  SubmitButton,
  FormField 
} from './styles';
import { 
  createSuperAdminSignupField, 
  updateSuperAdminSignupField,
  fetchSuperAdminDocumentTypes 
} from '../../../../../../modules/superadmin/actions';
import { RootState } from '../../../../../../store';
import { SignupField, DocumentType } from '../../../../../../modules/superadmin/types';
import { AppDispatch } from '../../../../../../store';

interface FieldModalProps {
  open: boolean;
  onClose: () => void;
  field?: SignupField | null;
}

const FieldModal: React.FC<FieldModalProps> = ({ open, onClose, field }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { documentTypes } = useSelector((state: RootState) => state.superadmin);
  
  const [formData, setFormData] = useState({
    label: '',
    name: '',
    field_type: 'text',
    is_required: false,
    order: 0,
    options: '',
    document_type: '' as string | number,
    is_active: true
  });

  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (open) {
      dispatch(fetchSuperAdminDocumentTypes({ search: '', page: 1, page_size: 100 }));
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (field) {
      setFormData({
        label: field.label,
        name: field.name,
        field_type: field.field_type,
        is_required: field.is_required,
        order: field.order,
        options: field.options || '',
        document_type: field.document_type || '',
        is_active: field.is_active
      });
    } else {
      setFormData({
        label: '',
        name: '',
        field_type: 'text',
        is_required: false,
        order: 0,
        options: '',
        document_type: '',
        is_active: true
      });
    }
    setErrors({});
  }, [field, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value, type, checked } = e.target as any;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // Auto-generate name from label if name is not a constant and we're not explicitly editing the name
      if (name === 'label' && !field?.is_constant) {
        const slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '');
        newData.name = slug;
      }

      return newData;
    });
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: any = {};
    if (!formData.label.trim()) newErrors.label = 'Label is required';
    if (!formData.name.trim()) newErrors.name = 'Internal name is required';
    if (formData.field_type === 'select' && !formData.options.trim()) {
      newErrors.options = 'Options are required for dropdown fields';
    }
    if (formData.field_type === 'file' && !formData.document_type) {
      newErrors.document_type = 'Document type is required for file uploads';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectChange = (e: SelectChangeEvent<string | number>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      ...formData,
      document_type: formData.field_type === 'file' ? formData.document_type : null,
      options: formData.field_type === 'select' ? formData.options : null
    };

    if (field) {
      dispatch(updateSuperAdminSignupField({ ...payload, field_id: field.field_id }));
    } else {
      dispatch(createSuperAdminSignupField(payload));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        {field ? 'Edit Signup Field' : 'Add New Signup Field'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <FormField>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Field Label *</Typography>
          <TextField
            name="label"
            value={formData.label}
            onChange={handleChange}
            placeholder="e.g. Company Registration Number"
            fullWidth
            size="small"
            error={!!errors.label}
            helperText={errors.label}
            disabled={field?.is_constant}
          />
        </FormField>

        <FormField>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Internal Name (Slug) *</Typography>
          <TextField
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. registration_number"
            fullWidth
            size="small"
            error={!!errors.name}
            helperText={errors.name}
            disabled={field?.is_constant}
          />
        </FormField>

        <FormField>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Field Type</Typography>
          <FormControl fullWidth size="small">
            <Select
              name="field_type"
              value={formData.field_type}
              onChange={handleSelectChange}
              disabled={field?.is_constant}
            >
              <MenuItem value="text">Short Text</MenuItem>
              <MenuItem value="textarea">Long Text</MenuItem>
              <MenuItem value="number">Number</MenuItem>
              <MenuItem value="select">Dropdown</MenuItem>
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="file">File Upload</MenuItem>
              <MenuItem value="password">Password</MenuItem>
            </Select>
          </FormControl>
        </FormField>

        {formData.field_type === 'select' && (
          <FormField>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Options (Comma separated) *</Typography>
            <TextField
              name="options"
              value={formData.options}
              onChange={handleChange}
              placeholder="Option 1, Option 2, Option 3"
              fullWidth
              size="small"
              error={!!errors.options}
              helperText={errors.options}
            />
          </FormField>
        )}

        {formData.field_type === 'file' && (
          <FormField>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Document Type *</Typography>
            <FormControl fullWidth size="small" error={!!errors.document_type}>
              <Select
                name="document_type"
                value={formData.document_type}
                onChange={handleSelectChange}
              >
                {documentTypes.map((doc: DocumentType) => (
                  <MenuItem key={doc.document_type_id} value={doc.document_type_id}>
                    {doc.document_type}
                  </MenuItem>
                ))}
              </Select>
              {errors.document_type && <Typography variant="caption" color="error">{errors.document_type}</Typography>}
            </FormControl>
          </FormField>
        )}

        <Box sx={{ display: 'flex', gap: 4 }}>
          <FormField sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Display Order</Typography>
            <TextField
              name="order"
              type="number"
              value={formData.order}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </FormField>

          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', pt: 2.5 }}>
            <FormControlLabel
              control={
                <Switch
                  name="is_required"
                  checked={formData.is_required}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Required</Typography>}
            />
          </Box>
        </Box>

        {!field?.is_constant && (
          <FormControlLabel
            control={
              <Switch
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                color="primary"
              />
            }
            label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Active</Typography>}
          />
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <SubmitButton onClick={handleSubmit} variant="contained">
          {field ? 'Update Field' : 'Create Field'}
        </SubmitButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default FieldModal;
